import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { Bouton, Etiquette, Liste, Saisie } from '../components/ui/Base'
import { EtatVide, MessageErreur } from '../components/ui/Etats'
import { SqueletteTableau } from '../components/ui/Squelette'
import { api } from '../lib/api/client'
import { cles } from '../lib/api/cles'
import type { Page, Patient } from '../lib/api/types'

const TAILLE = 20

export function Patients() {
  const [parametres, setParametres] = useSearchParams()
  const naviguer = useNavigate()
  const page = Number(parametres.get('page') ?? 1)
  const recherche = parametres.get('recherche') ?? ''
  const actif = parametres.get('actif') ?? ''
  const geocodage = parametres.get('geocodage_statut') ?? ''

  const requeteParams = {
    page,
    taille: TAILLE,
    ...(recherche ? { recherche } : {}),
    ...(actif ? { actif } : {}),
    ...(geocodage ? { geocodage_statut: geocodage } : {}),
  }

  const requete = useQuery({
    queryKey: cles.patients.liste(requeteParams),
    queryFn: ({ signal }) => {
      const url = new URLSearchParams()
      Object.entries(requeteParams).forEach(([c, v]) => url.set(c, String(v)))
      return api.get<Page<Patient>>(`/patients?${url.toString()}`, signal)
    },
  })

  function changer(cle: string, valeur: string) {
    const suivants = new URLSearchParams(parametres)
    if (valeur) suivants.set(cle, valeur)
    else suivants.delete(cle)
    // Tout changement de filtre ramène en page 1 : rester en page 4 d'un
    // résultat qui n'en compte plus que 2 afficherait une liste vide sans
    // raison apparente.
    if (cle !== 'page') suivants.delete('page')
    setParametres(suivants, { replace: true })
  }

  const total = requete.data?.total ?? 0
  const nbPages = Math.max(1, Math.ceil(total / TAILLE))

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Patients</h1>
          <p className="text-sm text-slate-600">{total} patients enregistrés</p>
        </div>
        <Bouton onClick={() => naviguer('/patients/nouveau')}>Ajouter un patient</Bouton>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-white p-3">
        <div className="min-w-56 flex-1">
          <label htmlFor="recherche" className="mb-1 block text-xs font-medium text-slate-600">
            Rechercher
          </label>
          <Saisie
            id="recherche"
            type="search"
            placeholder="Nom, prénom ou ville"
            value={recherche}
            onChange={(e) => changer('recherche', e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="actif" className="mb-1 block text-xs font-medium text-slate-600">
            Statut
          </label>
          <Liste id="actif" value={actif} onChange={(e) => changer('actif', e.target.value)}>
            <option value="">Tous</option>
            <option value="true">Actifs</option>
            <option value="false">Inactifs</option>
          </Liste>
        </div>
        <div>
          <label htmlFor="geo" className="mb-1 block text-xs font-medium text-slate-600">
            Géolocalisation
          </label>
          <Liste id="geo" value={geocodage} onChange={(e) => changer('geocodage_statut', e.target.value)}>
            <option value="">Toutes</option>
            <option value="OK">Géolocalisés</option>
            <option value="ECHEC">Échec</option>
            <option value="EN_ATTENTE">En attente</option>
            <option value="MANUEL">Manuel</option>
          </Liste>
        </div>
      </div>

      {requete.isPending ? (
        <SqueletteTableau colonnes={5} />
      ) : requete.isError ? (
        <MessageErreur erreur={requete.error} onReessayer={() => requete.refetch()} />
      ) : total === 0 ? (
        <EtatVide
          icone="🧑‍⚕️"
          titre={recherche ? 'Aucun patient ne correspond' : 'Aucun patient enregistré'}
          description={
            recherche
              ? 'Essayez avec moins de mots, ou vérifiez l’orthographe.'
              : 'Commencez par ajouter les patients suivis par le cabinet.'
          }
          action={
            recherche ? (
              <Bouton variante="secondaire" onClick={() => changer('recherche', '')}>
                Effacer la recherche
              </Bouton>
            ) : (
              <Bouton onClick={() => naviguer('/patients/nouveau')}>Ajouter un patient</Bouton>
            )
          }
        />
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <caption className="sr-only">Liste des patients du cabinet</caption>
              <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th scope="col" className="px-4 py-2.5">Nom</th>
                  <th scope="col" className="px-4 py-2.5">Adresse</th>
                  <th scope="col" className="px-4 py-2.5">Téléphone</th>
                  <th scope="col" className="px-4 py-2.5">Accès</th>
                  <th scope="col" className="px-4 py-2.5">État</th>
                </tr>
              </thead>
              <tbody>
                {requete.data.items.map((patient) => (
                  <tr key={patient.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                    <td className="px-4 py-2.5">
                      <Link to={`/patients/${patient.id}`} className="font-medium text-sky-700 hover:underline">
                        {patient.nom} {patient.prenom}
                      </Link>
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">
                      {patient.adresse}, {patient.code_postal} {patient.ville}
                    </td>
                    <td className="px-4 py-2.5 tabular-nums text-slate-600">{patient.telephone ?? '—'}</td>
                    <td className="px-4 py-2.5">
                      {patient.digicode ? (
                        <span className="font-mono text-xs text-slate-700">{patient.digicode}</span>
                      ) : (
                        <span className="text-xs text-slate-400">Non renseigné</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex flex-wrap gap-1">
                        {!patient.actif ? <Etiquette ton="neutre">Inactif</Etiquette> : null}
                        {patient.geocodage_statut !== 'OK' ? (
                          <Etiquette ton="attention">Non géolocalisé</Etiquette>
                        ) : null}
                        {patient.presence_animaux ? <Etiquette ton="danger">Animaux</Etiquette> : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <nav className="flex items-center justify-between" aria-label="Pagination">
            <p className="text-sm text-slate-600">
              Page {page} sur {nbPages}
            </p>
            <div className="flex gap-2">
              <Bouton
                variante="secondaire"
                disabled={page <= 1}
                onClick={() => changer('page', String(page - 1))}
              >
                Précédente
              </Bouton>
              <Bouton
                variante="secondaire"
                disabled={page >= nbPages}
                onClick={() => changer('page', String(page + 1))}
              >
                Suivante
              </Bouton>
            </div>
          </nav>
        </>
      )}
    </div>
  )
}
